import { useState, useMemo } from "react";
import { Link } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { 
  ArrowRight, Users, ClipboardCheck, UserCircle, Route, Cpu, Rocket, 
  Target, TrendingUp, Briefcase, Lightbulb, CheckCircle, DollarSign,
  Building2, GraduationCap, ChevronRight, Filter, Sparkles, Zap,
  BarChart3, Search, ExternalLink, ChevronDown, ChevronUp, Star,
  ListOrdered, CircleDot, Gauge, Shield, Clock, Boxes, Info, AlertCircle
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Progress } from "@/components/ui/progress";
import { useWorkforceIntelligence } from "@/contexts/WorkforceIntelligenceContext";
import BackNavigation from "@/components/BackNavigation";
import { 
  OPPORTUNITY_DATABASE, 
  OPPORTUNITY_CATEGORIES,
  DEMAND_LEVEL,
  DIFFICULTY_LEVEL,
  TOOL_STACKS,
  getToolById,
  getToolsByLab,
  getRecommendedToolsForOpportunity,
  getToolsForSkill,
  getStackTools,
  AI_REGISTRY
} from "@/data/aiRegistry";

/**
 * Opportunity Intelligence Engine
 * Converts workforce intelligence into actionable opportunities
 * 
 * Core Flow:
 * 1. Assessment Results (input)
 * 2. Profile Intelligence (analysis)
 * 3. Skill/Capability Mapping (matching)
 * 4. Opportunity Matching (logic)
 * 5. Output Recommendations (output)
 */
export default function OpportunityEngine() {
  const [activeCategory, setActiveCategory] = useState("all");
  const [expandedOpportunity, setExpandedOpportunity] = useState(null);

  // Get real user profile from WII context
  const { 
    hasCompletedAssessment, 
    wiiScore, 
    forceScores, 
    readinessLevel,
    scoreOpportunity,
    strengthDetails,
    gapDetails
  } = useWorkforceIntelligence();

  // User profile for scoring (real data if available)
  const userProfile = useMemo(() => ({
    waitScores: hasCompletedAssessment ? forceScores : null,
    wiiScore: hasCompletedAssessment ? wiiScore : null,
    readinessLevel: readinessLevel || null,
    currentSkills: [],
    experienceLevel: readinessLevel || null
  }), [hasCompletedAssessment, forceScores, wiiScore, readinessLevel]);

  /**
   * Calculate predicted fit score for an opportunity based on user's WII profile
   */
  const calculateFitScore = (opportunity) => {
    if (!hasCompletedAssessment || !forceScores) {
      return null;
    }

    let score = 50; // Base score
    let reasons = [];

    // Check WAIT requirements if available
    if (opportunity.waitScoreRequirements) {
      const reqs = opportunity.waitScoreRequirements;
      const reqForces = Object.keys(reqs);
      let matchCount = 0;
      let totalReqs = reqForces.length;

      reqForces.forEach(forceId => {
        const userScore = forceScores[forceId];
        const reqScore = reqs[forceId];
        if (userScore && userScore >= reqScore) {
          matchCount++;
          score += 10;
          reasons.push({ type: 'strength', text: `Strong ${forceId.replace('_', ' ')}` });
        } else if (userScore && userScore >= reqScore - 15) {
          matchCount += 0.5;
          score += 5;
        }
      });

      // Bonus for meeting most requirements
      if (matchCount >= totalReqs * 0.75) {
        score += 15;
      }
    }

    // Factor in overall WII score
    if (wiiScore) {
      if (wiiScore >= 70) {
        score += 15;
        reasons.push({ type: 'wii', text: 'High WII compatibility' });
      } else if (wiiScore >= 50) {
        score += 8;
      }
    }

    // Match against user strengths
    if (strengthDetails && strengthDetails.length > 0) {
      const strengthIds = strengthDetails.map(s => s.id);
      if (opportunity.waitScoreRequirements) {
        const reqForces = Object.keys(opportunity.waitScoreRequirements);
        const strengthMatches = reqForces.filter(f => strengthIds.includes(f));
        if (strengthMatches.length > 0) {
          score += strengthMatches.length * 8;
          reasons.push({ type: 'match', text: `Aligns with your strengths` });
        }
      }
    }

    // Adjust based on difficulty vs readiness
    if (opportunity.difficultyLevel && readinessLevel) {
      const difficultyMap = { beginner: 1, intermediate: 2, advanced: 3 };
      const readinessMap = { basic: 1, developing: 2, advanced: 3 };
      const diffLevel = difficultyMap[opportunity.difficultyLevel] || 2;
      const readLevel = readinessMap[readinessLevel] || 2;
      
      if (readLevel >= diffLevel) {
        score += 10;
        reasons.push({ type: 'ready', text: 'Matches your readiness level' });
      } else if (readLevel === diffLevel - 1) {
        score += 5;
        reasons.push({ type: 'stretch', text: 'Good stretch opportunity' });
      }
    }

    // Cap score at 100
    score = Math.min(100, Math.max(0, score));

    return {
      score: Math.round(score),
      reasons: reasons.slice(0, 3) // Top 3 reasons
    };
  };

  // Get all opportunities from database with fit scores
  const allOpportunities = useMemo(() => {
    const addFitScores = (opps) => opps.map(opp => ({
      ...opp,
      fitData: calculateFitScore(opp)
    }));

    return {
      business: addFitScores(OPPORTUNITY_DATABASE.business || []),
      service: addFitScores(OPPORTUNITY_DATABASE.service || []),
      career: addFitScores(OPPORTUNITY_DATABASE.career || [])
    };
  }, [hasCompletedAssessment, forceScores, wiiScore, readinessLevel]);

  // Filter opportunities based on active category
  const filteredOpportunities = useMemo(() => {
    if (activeCategory === "all") {
      return allOpportunities;
    }
    return {
      [activeCategory]: allOpportunities[activeCategory] || []
    };
  }, [activeCategory, allOpportunities]);

  // Get featured tool stack (Workforce Starter)
  const featuredStack = useMemo(() => {
    return TOOL_STACKS.find(s => s.id === "wf-starter") || TOOL_STACKS[0];
  }, []);

  // Category cards
  const categoryCards = [
    {
      id: "business",
      icon: Briefcase,
      title: "Business Opportunities",
      description: "AI-enabled ventures, consulting practices, and service businesses",
      count: allOpportunities.business.length,
      color: "#991B1B",
      examples: ["AI Consulting", "Content Agency", "Automation Services"]
    },
    {
      id: "service",
      icon: Target,
      title: "Service Pathways",
      description: "Freelance services, coaching, and specialized professional services",
      count: allOpportunities.service.length,
      color: "#2563EB",
      examples: ["AI Trainer", "Career Coach", "Research Analyst"]
    },
    {
      id: "career",
      icon: TrendingUp,
      title: "Career Pathways",
      description: "Employment roles, advancement paths, and career transitions",
      count: allOpportunities.career.length,
      color: "#1E3A8A",
      examples: ["AI Operations", "Workforce Analyst", "Learning Designer"]
    }
  ];

  // The engine flow steps
  const engineFlow = [
    {
      step: 1,
      icon: ClipboardCheck,
      title: "Assessment Results",
      description: "WAIT Theory diagnostic establishes your workforce intelligence baseline."
    },
    {
      step: 2,
      icon: UserCircle,
      title: "Profile Intelligence",
      description: "Scores and strengths create a comprehensive intelligence profile."
    },
    {
      step: 3,
      icon: Route,
      title: "Skill/Capability Mapping",
      description: "Capabilities mapped against market demands and AI tool requirements."
    },
    {
      step: 4,
      icon: Sparkles,
      title: "Opportunity Matching",
      description: "Algorithm connects your profile with relevant opportunities."
    },
    {
      step: 5,
      icon: Rocket,
      title: "Output Recommendations",
      description: "Prioritized recommendations with required skills and tools."
    }
  ];

  // Styling functions
  const getDemandStyle = (demand) => {
    const d = typeof demand === 'string' ? demand.toLowerCase() : '';
    if (d.includes('very high')) return { bg: "bg-green-100", text: "text-green-700", label: "Very High" };
    if (d.includes('high')) return { bg: "bg-blue-100", text: "text-blue-700", label: "High" };
    if (d.includes('medium')) return { bg: "bg-yellow-100", text: "text-yellow-700", label: "Medium" };
    return { bg: "bg-gray-100", text: "text-gray-700", label: "Low" };
  };

  const getBarrierStyle = (barrier) => {
    switch (barrier) {
      case "low": return { bg: "bg-green-50", text: "text-green-600", label: "Low Barrier" };
      case "medium": return { bg: "bg-yellow-50", text: "text-yellow-600", label: "Medium Barrier" };
      case "high": return { bg: "bg-red-50", text: "text-red-600", label: "High Barrier" };
      default: return { bg: "bg-gray-50", text: "text-gray-600", label: "Unknown" };
    }
  };

  const getDifficultyStyle = (level) => {
    const l = typeof level === 'string' ? level.toLowerCase() : '';
    if (l.includes('beginner')) return { bg: "bg-green-100", text: "text-green-700", label: "Beginner" };
    if (l.includes('intermediate')) return { bg: "bg-yellow-100", text: "text-yellow-700", label: "Intermediate" };
    if (l.includes('advanced')) return { bg: "bg-red-100", text: "text-red-700", label: "Advanced" };
    return { bg: "bg-gray-100", text: "text-gray-700", label: "Unknown" };
  };

  // Get match reason for a tool based on opportunity
  const getToolMatchReason = (tool, opportunity) => {
    if (!tool || !opportunity) return "Recommended tool";
    
    // Check if explicitly recommended
    if (opportunity.recommendedTools?.includes(tool.id)) {
      // Provide specific reason based on tool characteristics
      if (tool.scores?.reliability >= 90) return "High reliability for this opportunity";
      if (tool.scores?.workforceFit >= 90) return "Excellent workforce fit";
      if (tool.scores?.aiReadinessLevel === 'basic') return "Beginner-friendly tool";
      if (tool.category === 'content-writing') return "Matched for content creation";
      if (tool.category === 'automation-workflow') return "Matched for automation needs";
      if (tool.category === 'data-analytics') return "Matched for analytics requirements";
      if (tool.category === 'research-learning') return "Matched for research tasks";
      return "Specifically recommended";
    }
    
    // Skill-based match
    const matchingSkill = opportunity.requiredSkills?.find(skill => {
      const skillTools = getToolsForSkill(skill);
      return skillTools.some(t => t.id === tool.id);
    });
    
    if (matchingSkill) return `Matched for: ${matchingSkill}`;
    return "Related tool";
  };

  // Render WAIT Requirements as readiness indicators
  const renderWaitRequirements = (requirements) => {
    if (!requirements) return null;
    
    const indicators = [];
    if (requirements.minAiCapability) {
      indicators.push({ label: "AI Capability", value: requirements.minAiCapability, icon: Cpu });
    }
    if (requirements.minAlignment) {
      indicators.push({ label: "Alignment", value: requirements.minAlignment, icon: Target });
    }
    if (requirements.minTrust) {
      indicators.push({ label: "Trust", value: requirements.minTrust, icon: Shield });
    }
    if (requirements.minReadiness) {
      indicators.push({ label: "Readiness", value: requirements.minReadiness, icon: Gauge });
    }
    
    if (indicators.length === 0) return null;
    
    return (
      <div className="mt-3 pt-3 border-t border-[#E5E7EB]">
        <p className="text-xs font-medium text-[#1E3A8A] mb-2 flex items-center gap-1">
          <Info className="w-3 h-3" />
          Readiness Indicators
        </p>
        <div className="flex flex-wrap gap-2">
          {indicators.map((ind, i) => (
            <div 
              key={i}
              className="flex items-center gap-1.5 px-2 py-1 rounded bg-[#1E3A8A]/5 text-[#1E3A8A]"
              title={`Recommended minimum ${ind.label} score: ${ind.value}`}
            >
              <ind.icon className="w-3 h-3" />
              <span className="text-xs">{ind.label}</span>
              <span className="text-xs font-semibold">{ind.value}+</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  // Render opportunity card with expandable details
  const renderOpportunityCard = (opp, index, colorTheme) => {
    const demandStyle = getDemandStyle(opp.marketDemand || opp.growthOutlook);
    const barrierStyle = getBarrierStyle(opp.entryBarrier);
    const difficultyStyle = getDifficultyStyle(opp.difficultyLevel);
    const isExpanded = expandedOpportunity === opp.id;
    const recommendedTools = getRecommendedToolsForOpportunity(opp);
    const fitData = opp.fitData; // Get fit score from calculated data
    
    return (
      <motion.div
        key={opp.id}
        initial={{ opacity: 0, y: 20 }}
        whileInView={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, delay: Math.min(index * 0.1, 0.3) }}
        viewport={{ once: true }}
        data-testid={`opportunity-${opp.id}`}
      >
        <Card className={`border-[#E5E7EB] transition-all ${isExpanded ? 'shadow-lg border-l-4' : 'hover:shadow-lg'}`} style={isExpanded ? { borderLeftColor: colorTheme } : {}}>
          <CardContent className="p-0">
            {/* Predicted Fit Score Badge - only if user has completed assessment */}
            {fitData && (
              <div 
                className="flex items-center justify-between px-6 py-2 border-b"
                style={{ 
                  backgroundColor: fitData.score >= 70 ? '#ecfdf5' : fitData.score >= 50 ? '#fefce8' : '#fef2f2',
                  borderColor: fitData.score >= 70 ? '#10b981' : fitData.score >= 50 ? '#eab308' : '#f87171'
                }}
              >
                <div className="flex items-center gap-2">
                  <Sparkles className="w-4 h-4" style={{ color: fitData.score >= 70 ? '#10b981' : fitData.score >= 50 ? '#eab308' : '#f87171' }} />
                  <span className="text-sm font-semibold" style={{ color: fitData.score >= 70 ? '#065f46' : fitData.score >= 50 ? '#713f12' : '#991b1b' }}>
                    Predicted Fit: {fitData.score}%
                  </span>
                </div>
                <div className="flex items-center gap-1">
                  {fitData.reasons?.slice(0, 2).map((reason, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded bg-white/60 text-gray-700">
                      {reason.text}
                    </span>
                  ))}
                </div>
              </div>
            )}
            
            {/* Main Card Content */}
            <div className="p-6">
              <div className="flex items-start justify-between mb-3">
                <div>
                  <h4 className="font-heading text-lg font-bold" style={{ color: colorTheme }}>
                    {opp.name}
                  </h4>
                  {opp.difficultyLevel && (
                    <span className={`inline-block mt-1 text-xs px-2 py-0.5 rounded ${difficultyStyle.bg} ${difficultyStyle.text}`}>
                      {difficultyStyle.label} Level
                    </span>
                  )}
                </div>
                <span className={`text-xs px-2 py-0.5 rounded-full ${demandStyle.bg} ${demandStyle.text} font-medium`}>
                  {demandStyle.label} {opp.growthOutlook ? 'Growth' : 'Demand'}
                </span>
              </div>
              
              <p className="text-sm text-[#1E3A8A]/70 mb-4">
                {opp.description}
              </p>
              
              {/* Required Skills */}
              <div className="mb-3">
                <p className="text-xs font-medium text-[#1E3A8A] mb-1">Required Skills</p>
                <div className="flex flex-wrap gap-1">
                  {opp.requiredSkills?.map((skill, i) => (
                    <span key={i} className="text-xs px-2 py-0.5 rounded bg-[#F8FAFC] text-[#1E3A8A]/70">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
              
              {/* Recommended AI Tools with Match Reasons */}
              <div className="mb-3">
                <p className="text-xs font-medium text-[#1E3A8A] mb-1">Recommended AI Tools</p>
                <div className="space-y-1">
                  {recommendedTools.slice(0, 3).map(({ tool, matchType, matchReason }, i) => (
                    <div key={i} className="flex items-center gap-2">
                      <Link 
                        to={`/observatories/ai/registry?search=${tool.name}`}
                        className="text-xs px-2 py-0.5 rounded text-[#1E3A8A] hover:bg-[#1E3A8A]/20 flex items-center gap-1"
                        style={{ backgroundColor: `${colorTheme}15` }}
                      >
                        <Cpu className="w-3 h-3" />
                        {tool.name}
                      </Link>
                      <span className="text-[10px] text-[#1E3A8A]/50 italic">
                        {matchReason || getToolMatchReason(tool, opp)}
                      </span>
                    </div>
                  ))}
                </div>
              </div>
              
              {/* WAIT Readiness Indicators */}
              {renderWaitRequirements(opp.waitScoreRequirements)}
              
              {/* Footer */}
              <div className="flex items-center justify-between pt-4 mt-4 border-t border-[#E5E7EB]">
                <div className="flex items-center gap-1 text-sm font-semibold text-green-600">
                  <DollarSign className="w-4 h-4" />
                  {opp.potentialRevenue || opp.hourlyRate || opp.salaryRange}
                </div>
                <div className="flex items-center gap-2">
                  {opp.entryBarrier && (
                    <span className={`text-xs px-2 py-0.5 rounded ${barrierStyle.bg} ${barrierStyle.text}`}>
                      {barrierStyle.label}
                    </span>
                  )}
                  {opp.pathwaySteps && (
                    <button
                      onClick={() => setExpandedOpportunity(isExpanded ? null : opp.id)}
                      className="flex items-center gap-1 text-xs font-medium hover:underline"
                      style={{ color: colorTheme }}
                      data-testid={`expand-${opp.id}`}
                    >
                      {isExpanded ? 'Hide Steps' : 'View Pathway'}
                      {isExpanded ? <ChevronUp className="w-4 h-4" /> : <ChevronDown className="w-4 h-4" />}
                    </button>
                  )}
                </div>
              </div>
            </div>
            
            {/* Expanded Pathway Steps */}
            <AnimatePresence>
              {isExpanded && opp.pathwaySteps && (
                <motion.div
                  initial={{ height: 0, opacity: 0 }}
                  animate={{ height: "auto", opacity: 1 }}
                  exit={{ height: 0, opacity: 0 }}
                  transition={{ duration: 0.3 }}
                  className="overflow-hidden"
                >
                  <div className="px-6 pb-6 pt-2 bg-[#F8FAFC] border-t border-[#E5E7EB]">
                    <div className="flex items-center gap-2 mb-4">
                      <ListOrdered className="w-4 h-4" style={{ color: colorTheme }} />
                      <h5 className="text-sm font-bold" style={{ color: colorTheme }}>
                        Pathway to Success ({opp.pathwaySteps.length} Steps)
                      </h5>
                    </div>
                    <div className="space-y-3">
                      {opp.pathwaySteps.map((step, idx) => (
                        <div key={idx} className="flex gap-3">
                          <div 
                            className="w-7 h-7 rounded-full flex items-center justify-center text-white text-xs font-bold flex-shrink-0"
                            style={{ backgroundColor: colorTheme }}
                          >
                            {step.step}
                          </div>
                          <div className="flex-1 pt-0.5">
                            <p className="text-sm font-medium text-[#1E3A8A]">{step.title}</p>
                            <p className="text-xs text-[#1E3A8A]/60 mt-0.5">{step.description}</p>
                          </div>
                        </div>
                      ))}
                    </div>
                    
                    {/* Industry Fit */}
                    {opp.industryFit && (
                      <div className="mt-4 pt-3 border-t border-[#E5E7EB]">
                        <p className="text-xs font-medium text-[#1E3A8A] mb-2">Best For Industries</p>
                        <div className="flex flex-wrap gap-1">
                          {opp.industryFit.map((industry, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 rounded bg-white text-[#1E3A8A]/70 border border-[#E5E7EB]">
                              {industry}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                    
                    {/* Career Pathway From */}
                    {opp.pathwayFrom && (
                      <div className="mt-4 pt-3 border-t border-[#E5E7EB]">
                        <p className="text-xs font-medium text-[#1E3A8A] mb-2">Common Transition From</p>
                        <div className="flex flex-wrap gap-1">
                          {opp.pathwayFrom.map((role, i) => (
                            <span key={i} className="text-xs px-2 py-0.5 rounded bg-white border border-[#E5E7EB]" style={{ color: colorTheme }}>
                              {role}
                            </span>
                          ))}
                        </div>
                      </div>
                    )}
                  </div>
                </motion.div>
              )}
            </AnimatePresence>
          </CardContent>
        </Card>
      </motion.div>
    );
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]" data-testid="opportunity-engine-page">
      {/* Back Navigation */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-4">
        <BackNavigation fallbackPath="/zodiac-lab/workforce" fallbackLabel="Workforce Lab" />
      </div>

      {/* Hero Section */}
      <section className="bg-gradient-to-br from-[#991B1B] to-[#7F1D1D] text-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-20">
          <div className="flex items-center gap-2 mb-6">
            <Link to="/zodiac-lab" className="text-sm text-white/60 hover:text-white">
              Zodiac Lab
            </Link>
            <span className="text-white/40">/</span>
            <Link to="/zodiac-lab/workforce" className="text-sm text-white/60 hover:text-white">
              Workforce Lab
            </Link>
            <span className="text-white/40">/</span>
            <span className="text-sm text-white font-medium">Opportunity Engine</span>
          </div>
          
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/10 text-white text-sm font-medium mb-6">
              <Rocket className="w-4 h-4" />
              Intelligence-Driven Opportunities
            </span>
            <h1 className="font-heading text-4xl sm:text-5xl font-bold tracking-tight mb-6">
              Opportunity Intelligence Engine
            </h1>
            <p className="text-xl text-white/90 leading-relaxed mb-4">
              Transform your workforce intelligence into actionable opportunities.
            </p>
            <p className="text-base text-white/70 leading-relaxed mb-8">
              The Opportunity Intelligence Engine connects your assessment results, skills, and AI capabilities 
              with curated business opportunities, service pathways, and career directions matched to your profile.
            </p>
            <div className="flex flex-wrap gap-4">
              <Link to="/observatories/global/survey">
                <Button size="lg" className="bg-white text-[#991B1B] hover:bg-white/90">
                  Start Assessment
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
              </Link>
              <a href="#opportunities">
                <Button size="lg" variant="outline" className="border-white/30 text-white hover:bg-white/10">
                  View Opportunities
                </Button>
              </a>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Engine Flow Section */}
      <section className="py-16 bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
              How the Engine Works
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              Five-stage process that converts your workforce intelligence into matched opportunities.
            </p>
          </div>

          <div className="relative">
            <div className="hidden lg:block absolute top-1/2 left-0 right-0 h-0.5 bg-[#E5E7EB] -translate-y-1/2 z-0" />
            
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-6">
              {engineFlow.map((step, index) => (
                <motion.div
                  key={step.step}
                  initial={{ opacity: 0, y: 20 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  viewport={{ once: true }}
                  className="relative z-10"
                >
                  <Card className="h-full border-[#E5E7EB] bg-white hover:shadow-lg transition-shadow">
                    <CardContent className="p-5 text-center">
                      <div className="w-12 h-12 rounded-full bg-[#991B1B] text-white flex items-center justify-center mx-auto mb-4 text-lg font-bold">
                        {step.step}
                      </div>
                      <div className="w-10 h-10 rounded-lg bg-[#1E3A8A]/10 flex items-center justify-center mx-auto mb-3">
                        <step.icon className="w-5 h-5 text-[#1E3A8A]" />
                      </div>
                      <h3 className="font-heading text-sm font-bold text-[#991B1B] mb-2">
                        {step.title}
                      </h3>
                      <p className="text-xs text-[#1E3A8A]/70 leading-relaxed">
                        {step.description}
                      </p>
                    </CardContent>
                  </Card>
                </motion.div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Featured Tool Stack Section */}
      <section className="py-12 bg-[#1E3A8A]/5 border-b border-[#E5E7EB]" data-testid="featured-stack-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col lg:flex-row gap-8 items-center">
            <div className="lg:w-1/3">
              <div className="flex items-center gap-2 mb-3">
                <Boxes className="w-5 h-5 text-[#991B1B]" />
                <span className="text-sm font-medium text-[#991B1B]">Featured Tool Stack</span>
              </div>
              <h3 className="font-heading text-2xl font-bold mb-3" style={{ color: '#1E3A8A' }}>
                {featuredStack?.name}
              </h3>
              <p className="text-[#1E3A8A]/70 mb-4">
                {featuredStack?.description || featuredStack?.purpose}
              </p>
              <Link to="/observatories/ai/registry?tab=stacks">
                <Button variant="outline" className="border-[#1E3A8A] text-[#1E3A8A]">
                  View All Stacks
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
            
            <div className="lg:w-2/3">
              <Card className="border-[#E5E7EB] bg-white" data-testid="featured-stack-card">
                <CardContent className="p-6">
                  <div className="grid md:grid-cols-2 gap-6">
                    {/* Tools Included */}
                    <div>
                      <p className="text-xs font-medium text-[#1E3A8A] mb-3">Tools Included ({featuredStack?.tools?.length})</p>
                      <div className="space-y-2">
                        {getStackTools(featuredStack?.id)?.map((tool, i) => (
                          <div key={i} className="flex items-center gap-2 p-2 rounded bg-[#F8FAFC]">
                            <div className="w-8 h-8 rounded bg-[#1E3A8A]/10 flex items-center justify-center">
                              <Cpu className="w-4 h-4 text-[#1E3A8A]" />
                            </div>
                            <div>
                              <p className="text-sm font-medium text-[#1E3A8A]">{tool?.name}</p>
                              <p className="text-xs text-[#1E3A8A]/50">{tool?.provider}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                    
                    {/* Stack Details */}
                    <div className="space-y-4">
                      <div>
                        <p className="text-xs font-medium text-[#1E3A8A] mb-2">Expected Outcomes</p>
                        <ul className="space-y-1">
                          {featuredStack?.outcomes?.slice(0, 4).map((outcome, i) => (
                            <li key={i} className="flex items-start gap-2 text-sm text-[#1E3A8A]/70">
                              <CheckCircle className="w-4 h-4 text-green-500 mt-0.5 flex-shrink-0" />
                              {outcome}
                            </li>
                          ))}
                        </ul>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4 pt-4 border-t border-[#E5E7EB]">
                        <div>
                          <p className="text-xs text-[#1E3A8A]/50 mb-1">Setup Time</p>
                          <div className="flex items-center gap-1">
                            <Clock className="w-4 h-4 text-[#1E3A8A]" />
                            <span className="text-sm font-medium text-[#1E3A8A]">{featuredStack?.estimatedSetupTime}</span>
                          </div>
                        </div>
                        <div>
                          <p className="text-xs text-[#1E3A8A]/50 mb-1">Monthly Investment</p>
                          <div className="flex items-center gap-1">
                            <DollarSign className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-medium text-green-600">{featuredStack?.monthlyInvestment}</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </div>
      </section>

      {/* Opportunity Categories */}
      <section className="py-16 lg:py-20">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Assessment Status Banner */}
          {!hasCompletedAssessment && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 p-4 rounded-xl bg-gradient-to-r from-[#991B1B]/10 to-[#1E3A8A]/10 border border-[#E5E7EB]"
            >
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-[#991B1B]/20 flex items-center justify-center">
                    <AlertCircle className="w-5 h-5 text-[#991B1B]" />
                  </div>
                  <div>
                    <p className="font-semibold text-[#1E3A8A]">Get Personalized Recommendations</p>
                    <p className="text-sm text-[#1E3A8A]/70">Complete the assessment to see predicted fit scores for each opportunity.</p>
                  </div>
                </div>
                <Link to="/observatories/global/survey">
                  <Button className="bg-[#991B1B] text-white hover:bg-[#991B1B]/90">
                    <Sparkles className="w-4 h-4 mr-2" />
                    Take Assessment
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}
          
          {/* Assessment Completed Banner */}
          {hasCompletedAssessment && (
            <motion.div
              initial={{ opacity: 0, y: -10 }}
              animate={{ opacity: 1, y: 0 }}
              className="mb-8 p-4 rounded-xl bg-gradient-to-r from-green-50 to-emerald-50 border border-green-200"
            >
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-green-100 flex items-center justify-center">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                  </div>
                  <div>
                    <p className="font-semibold text-green-800">Personalized Recommendations Active</p>
                    <p className="text-sm text-green-700">
                      Your WII Score: <span className="font-bold">{wiiScore}</span> • 
                      Opportunities ranked by your profile fit
                    </p>
                  </div>
                </div>
                <Link to="/juswork/profile">
                  <Button variant="outline" className="border-green-500 text-green-700 hover:bg-green-50">
                    View Profile
                    <ArrowRight className="w-4 h-4 ml-2" />
                  </Button>
                </Link>
              </div>
            </motion.div>
          )}

          <div className="text-center mb-12">
            <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-4" style={{ color: '#991B1B' }}>
              Three Opportunity Pathways
            </h2>
            <p className="text-lg text-[#1E3A8A] max-w-2xl mx-auto">
              Discover opportunities across business creation, professional services, and career advancement.
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {categoryCards.map((category, index) => (
              <motion.div
                key={category.id}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                viewport={{ once: true }}
              >
                <Card 
                  className={`h-full border-2 cursor-pointer transition-all ${
                    activeCategory === category.id 
                      ? 'border-[#991B1B] shadow-lg' 
                      : 'border-[#E5E7EB] hover:border-[#1E3A8A]/30 hover:shadow-md'
                  }`}
                  onClick={() => setActiveCategory(activeCategory === category.id ? "all" : category.id)}
                  data-testid={`category-card-${category.id}`}
                >
                  <CardContent className="p-6">
                    <div 
                      className="w-14 h-14 rounded-xl flex items-center justify-center mb-4"
                      style={{ backgroundColor: `${category.color}15` }}
                    >
                      <category.icon className="w-7 h-7" style={{ color: category.color }} />
                    </div>
                    <div className="flex items-center justify-between mb-2">
                      <h3 className="font-heading text-lg font-bold" style={{ color: category.color }}>
                        {category.title}
                      </h3>
                      <Badge variant="secondary" className="bg-[#F8FAFC]">
                        {category.count}
                      </Badge>
                    </div>
                    <p className="text-sm text-[#1E3A8A]/70 mb-4">
                      {category.description}
                    </p>
                    <div className="flex flex-wrap gap-1">
                      {category.examples.map((example, i) => (
                        <span 
                          key={i} 
                          className="text-xs px-2 py-0.5 rounded-full bg-[#F8FAFC] text-[#1E3A8A]/60"
                        >
                          {example}
                        </span>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Opportunity Listings */}
      <section id="opportunities" className="py-16 lg:py-20 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
            <div>
              <h2 className="font-heading text-2xl sm:text-3xl font-bold mb-2" style={{ color: '#991B1B' }}>
                {activeCategory === "all" ? "All Opportunities" : categoryCards.find(c => c.id === activeCategory)?.title}
              </h2>
              <p className="text-[#1E3A8A]/70">
                {activeCategory === "all" 
                  ? "Explore all opportunity pathways matched to workforce intelligence profiles"
                  : `Showing ${filteredOpportunities[activeCategory]?.length || 0} opportunities`
                }
              </p>
            </div>
            {activeCategory !== "all" && (
              <Button 
                variant="outline" 
                onClick={() => setActiveCategory("all")}
                className="border-[#1E3A8A] text-[#1E3A8A]"
              >
                View All
              </Button>
            )}
          </div>

          {/* Business Opportunities */}
          {(activeCategory === "all" || activeCategory === "business") && filteredOpportunities.business?.length > 0 && (
            <div className="mb-12">
              {activeCategory === "all" && (
                <div className="flex items-center gap-3 mb-6">
                  <Briefcase className="w-6 h-6 text-[#991B1B]" />
                  <h3 className="font-heading text-xl font-bold text-[#991B1B]">Business Opportunities</h3>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-6">
                {filteredOpportunities.business.map((opp, index) => 
                  renderOpportunityCard(opp, index, "#991B1B")
                )}
              </div>
            </div>
          )}

          {/* Service Pathways */}
          {(activeCategory === "all" || activeCategory === "service") && filteredOpportunities.service?.length > 0 && (
            <div className="mb-12">
              {activeCategory === "all" && (
                <div className="flex items-center gap-3 mb-6">
                  <Target className="w-6 h-6 text-[#2563EB]" />
                  <h3 className="font-heading text-xl font-bold text-[#2563EB]">Service Pathways</h3>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-6">
                {filteredOpportunities.service.map((opp, index) => 
                  renderOpportunityCard(opp, index, "#2563EB")
                )}
              </div>
            </div>
          )}

          {/* Career Pathways */}
          {(activeCategory === "all" || activeCategory === "career") && filteredOpportunities.career?.length > 0 && (
            <div className="mb-12">
              {activeCategory === "all" && (
                <div className="flex items-center gap-3 mb-6">
                  <TrendingUp className="w-6 h-6 text-[#1E3A8A]" />
                  <h3 className="font-heading text-xl font-bold text-[#1E3A8A]">Career Pathways</h3>
                </div>
              )}
              <div className="grid md:grid-cols-2 gap-6">
                {filteredOpportunities.career.map((opp, index) => 
                  renderOpportunityCard(opp, index, "#1E3A8A")
                )}
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Future Personalization Note */}
      <section className="py-12 bg-[#1E3A8A]/5 border-y border-[#E5E7EB]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <div className="flex items-center justify-center gap-2 mb-4">
            <Sparkles className="w-5 h-5 text-[#991B1B]" />
            <span className="text-sm font-medium text-[#991B1B]">Personalization Ready</span>
          </div>
          <p className="text-[#1E3A8A]">
            Complete the workforce intelligence assessment to unlock <strong>personalized recommendations</strong> based on your WAIT scores.
            The matching engine will rank opportunities according to your <strong>AI capability</strong>, <strong>alignment</strong>, 
            and <strong>readiness levels</strong> for precision opportunity matching.
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#991B1B]">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-2xl sm:text-3xl font-bold text-white mb-4">
            Unlock Personalized Opportunities
          </h2>
          <p className="text-white/80 mb-8 max-w-2xl mx-auto">
            Complete the workforce intelligence assessment to receive recommendations matched to your unique profile.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link to="/observatories/global/survey">
              <Button size="lg" className="bg-white text-[#991B1B] hover:bg-white/90 h-14 px-8">
                Take Assessment
                <ArrowRight className="w-5 h-5 ml-2" />
              </Button>
            </Link>
            <Link to="/zodiac-lab/workforce">
              <Button size="lg" variant="outline" className="border-white text-white hover:bg-white/10 h-14 px-8">
                Back to Workforce Lab
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
